<template>
  <div class="flex flex-col h-full" v-if="techMap">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold flex items-center flex-1 mr-4">
        <template v-if="isEditingName">
          <input
            ref="techMapNameInput"
            type="text"
            v-model="techMap.name"
            maxlength="255"
            @input="hasUnsavedChanges = true"
            @blur="isEditingName = false"
            @keyup.enter="isEditingName = false"
            placeholder="Название спецификации"
            class="text-xl font-semibold bg-transparent border border-transparent rounded px-2 py-1 hover:border-gray-300 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-0"
          />
        </template>
        <template v-else>
          <span class="pl-2 py-1">{{ techMap?.name }}</span>
          <button
            v-can-write="productionModuleId"
            type="button"
            title="Редактировать название"
            class="p-1 text-blue-600 hover:text-blue-800 transition-colors duration-200"
            @click="startEditingName"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </template>
        <span v-if="techMap?.product?.accounting_type === 'single'"
          class="ml-2 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded whitespace-nowrap">
          Серийная продукция
        </span>
      </h2>
      <div class="flex space-x-2">
        <button
          @click="goBack"
          class="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center"
        >
          Назад
        </button>
        <button
          v-can-write="productionModuleId"
          @click="revertChanges"
          class="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600 flex items-center"
          :class="{'opacity-50 cursor-not-allowed': !hasUnsavedChanges || isSaving}"
          :disabled="!hasUnsavedChanges || isSaving"
        >
          <span>Отменить изменения</span>
        </button>
        <button
          v-can-write="productionModuleId"
          @click="showSaveConfirmModal"
          class="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center"
          :class="{'opacity-50 cursor-not-allowed': !hasUnsavedChanges || isSaving, 'animate-pulse': hasUnsavedChanges}"
          :disabled="!hasUnsavedChanges || isSaving"
        >
          <span v-if="isSaving">Сохранение...</span>
          <span v-else>Сохранить</span>
          <span v-if="hasUnsavedChanges" class="ml-2 h-2 w-2 bg-red-500 rounded-full"></span>
        </button>
      </div>
    </div>

    <div class="flex">
      <!-- Левая колонка (структура изделия) -->
      <div class="w-1/2 border-r border-gray-200 p-4">
        <div class="flex items-center space-x-2 mb-2">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Поиск..."
            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
          <Dropdown
            v-can-write="productionModuleId"
            :options="addChildOptions"
            placeholder="Добавить"
            label="label"
            value-key="value"
            @select="handleOptionSelect"
          />
        </div>

        <ul>
          <TreeNode
            v-for="(fileItem, index) in filteredFiles"
            :key="`file-${index}`"
            :node="{ file: fileItem }"
            :number="index + 1"
            @viewDetailFile="viewDetailFile"
          />
          <li v-if="filteredFiles.length && filteredTechnologies.length" class="list-none my-2">
            <div class="border-b border-gray-200"></div>
          </li>
          <TreeNode
            v-for="(component, index) in filteredTechnologies"
            :key="`technology-${index}`"
            :node="component"
            :activeProductId="activeProductId"
            :activeTechnologyName="activeTechnologyName"
            :activeOperationId="activeOperationId"
            :activeTechnologyId="activeTechnologyId"
            :hoveredTechnologyId="hoveredTechnologyId"
            @hoverTechnology="setHoveredTechnology"
            @viewDetailTechnology="viewDetailTechnology"
            @viewDetailOperation="viewDetailOperation"
            @viewDetailStep="viewDetailStep"
          />
        </ul>
      </div>
      <!-- Правая колонка (форма редактирования) — sticky с внутренним скроллом -->
      <div data-detail-panel class="w-1/2 sticky top-0 self-start overflow-y-auto max-h-[calc(100vh-2rem)] p-4">
        <div class="border-b border-gray-200">
          <nav class="flex space-x-4" aria-label="Tabs">
            <button
              v-for="(tab, index) in tabs"
              :key="index"
              @click="switchTab(tab.name)"
              :class="[
                activeTab === tab.name
                  ? 'border-gray-300 text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-2 px-4 border rounded font-medium text-sm focus:outline-none w-full'
                ]"
            >
              {{ tab.label }}
            </button>
          </nav>
        </div>
        <div>
          <div v-if="activeTab === 'tab1'">
            <MaterialTab
              :data="aggregatedMaterials"
              class="p-4" />
          </div>
          <div v-if="activeTab === 'tab2'">
            <TechnologyTab
              v-if="activeComponent === null"
              :data="technologies"
              @removeTechnology="removeTechnology"
              @viewDetail="viewDetailTechnology"
              @removeOperation="removeOperation"
              @moveOperationUp="moveOperationUp"
              @moveOperationDown="moveOperationDown"
              class="p-4" />
            <ViewDetailTechnology
              v-if="activeComponent === 'viewDetailTechnology'"
              :data="selectedTechnology"
              @removeOperation="removeOperation"
              @viewDetail="viewDetailOperation"
              @moveOperationUp="moveOperationUp"
              @moveOperationDown="moveOperationDown"
              @change="handleTechnologyChange"
              class="p-4" />
            <template v-if="selectedOperation && workshopPosts.length > 0">
              <ViewDetailOperation
                v-if="documentsLoaded"
                :data="selectedOperation"
                :workshop-posts="workshopPosts"
                :products="productOptions"
                :documents="documents"
                :loading-products="isLoadingProducts"
                :tech-map-id="techMap.id"
                @change="handleOperationChange"
                @reload-products="reloadProducts"
                @reload-techmap="getTechMap"
                @copy-step="openCopyStepModal"
                @search="onProductSearch"
              />
              <div v-else class="loading-data">
                <p>Загрузка документов...</p>
              </div>
            </template>
            <template v-else>
              <div class="loading-data">
                <p v-if="!selectedOperation">Загрузка данных операции...</p>
                <p v-else-if="workshopPosts.length === 0">Загрузка данных рабочих постов...</p>
                <p v-else-if="isLoadingProducts">Перезагрузка списка продуктов...</p>
                <p v-else-if="apiProducts.length === 0">Загрузка списка продуктов...</p>
              </div>
            </template>
          </div>
          <div v-if="activeTab === 'tab3'" class="p-4">
            <h2 class="text-lg font-semibold mb-4">Документы</h2>
            <p v-if="!filteredFiles.length" class="text-gray-500 text-sm">Нет загруженных документов</p>
            <table v-else class="w-full border-collapse bg-white shadow-md">
              <thead class="bg-gray-100">
                <tr>
                  <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
                  <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название</th>
                  <th class="px-4 py-2 text-left text-sm font-medium text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(file, index) in filteredFiles" :key="file.id" class="border-b hover:bg-gray-50">
                  <td class="px-4 py-2 text-sm text-gray-700">{{ index + 1 }}</td>
                  <td class="px-4 py-2 text-sm text-gray-700">{{ file.original_name || file.name }}</td>
                  <td class="px-4 py-2">
                    <div class="flex justify-end space-x-2 items-center">
                      <button type="button" @click="openFilePreview(file)" class="p-1.5 text-blue-600 hover:text-blue-800 rounded hover:bg-blue-50" title="Открыть">
                        <ArrowTopRightOnSquareIcon class="w-5 h-5" />
                      </button>
                      <DeleteButton v-can-write="productionModuleId" :title="'документ'" @click="removeFile(index)" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>

    <!-- Модальное окно -->
    <div v-if="isModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <div v-if="selectedChildOption === 'material'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Название {{ getSelectedOptionLabel() }}</label>
          <v-select
            :options="productOptions"
            label="label"
            :filter="(options) => options || []"
            v-model="newItemName"
            placeholder="Начните вводить название или артикул..."
            :loading="isLoadingProducts"
            @search="onProductSearch"
            class="mt-1"
          >
            <template #no-options>
              <span v-if="isLoadingProducts">Загружается...</span>
              <span v-else>Ничего не найдено</span>
            </template>
          </v-select>
          <p v-if="addProductError" class="text-red-500 text-sm mt-1">{{ addProductError }}</p>
        </div>
        <div v-else-if="selectedChildOption === 'technology'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Название {{ getSelectedOptionLabel() }}</label>
          <textarea
            v-model="newItemName"
            rows="3"
            maxlength="255"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите название технологии"
          />
          <p v-if="addProductError" class="text-red-500 text-sm mt-1">{{ addProductError }}</p>
        </div>
        <div v-else-if="selectedChildOption === 'file'" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Загрузить PDF-документ</label>
          <p class="text-sm text-gray-500 mb-2">Документы будут напечатаны при запуске заказа</p>
          <div
            class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            :class="{ 'border-blue-500 bg-blue-50': isFileDragOver }"
            @dragover.prevent="isFileDragOver = true"
            @dragleave.prevent="isFileDragOver = false"
            @drop.prevent="handleFileDrop"
            @click="$refs.fileInput?.click()"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,application/pdf"
              multiple
              class="hidden"
              @change="handleFileSelect"
            />
            <svg class="w-12 h-12 mx-auto text-gray-400 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <p class="text-gray-600">Перетащите PDF сюда или нажмите для выбора</p>
          </div>
          <div v-if="fileUploadProgress" class="mt-2 text-sm text-gray-600">{{ fileUploadProgress }}</div>
          <ul v-if="pendingFiles.length" class="mt-3 space-y-2">
            <li v-for="(f, i) in pendingFiles" :key="f?.id ?? i" class="flex items-center justify-between p-2 bg-gray-50 rounded">
              <span class="text-sm text-gray-800 truncate">{{ f.original_name || f.name }}</span>
              <button type="button" @click="pendingFiles.splice(i, 1)" class="text-red-500 hover:text-red-700 text-sm ml-2">Удалить</button>
            </li>
          </ul>
          <p v-if="addProductError" class="text-red-500 text-sm mt-1">{{ addProductError }}</p>
        </div>
        <div v-else class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Выберите технологию</label>
          <v-select
            :options="technologyOptions"
            label="label"
            :reduce="option => option.value"
            v-model="selectedTechnologyForOperation"
            placeholder="Выберите технологию"
            class="mt-1"
          >
            <template #no-options="{ search, searching, loading }">
              К сожалению, ничего не найдено.
            </template>
          </v-select>

          <label class="block text-sm font-medium text-gray-700 mt-4">Выберите пост</label>
          <v-select
            v-if="workshopPosts"
            :options="workshopPostOptions"
            label="name"
            :reduce="option => option.id"
            v-model="selectedWorkshopPost"
            placeholder="Выберите пост"
            class="mt-1"
          >
            <template #no-options="{ search, searching, loading }">
              К сожалению, ничего не найдено.
            </template>
          </v-select>
          <label class="block text-sm font-medium text-gray-700 mt-4">Название {{ getSelectedOptionLabel() }}</label>
          <input
            v-model="newItemName"
            type="text"
            maxlength="255"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите название операции"
          />
          <p v-if="addProductError" class="text-red-500 text-sm mt-1">{{ addProductError }}</p>        </div>
        <div class="flex justify-end space-x-2">
          <button
            v-if="selectedChildOption === 'file'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!pendingFiles.length"
            @click="confirmFileUpload"
          >
            Подтвердить
          </button>
          <button
            v-else
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
            @click="addNewItem"
          >
            Сохранить
          </button>
          <button
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="closeModal"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>

    <!-- Модальное окно подтверждения -->
    <div v-if="isSaveConfirmModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-xl font-semibold mb-4">Подтвердите сохранение</h2>
        <p>Вы уверены, что хотите сохранить все несохраненные изменения?</p>
        <div class="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            @click="confirmSave"
          >
            Сохранить
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            @click="closeSaveConfirmModal"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка копирования шага -->
    <CopyStepModal
      v-if="copyStepModalOpen && copyStepSource"
      :step="copyStepSource"
      :technologies="technologies"
      @close="closeCopyStepModal"
      @confirm="onStepCopyConfirmed"
    />

    <!-- Модальное окно подтверждения отмены -->
    <div v-if="isCancelConfirmModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-xl font-semibold mb-4">Подтвердите отмену</h2>
        <p>Вы уверены, что хотите отменить все несохраненные изменения?</p>
        <div class="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            @click="confirmCancel"
          >
            Отменить
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            @click="closeCancelConfirmModal"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TreeNode from "@/components/production/TreeNode.vue";
import Specifications from "@/components/production/TechMaps.vue";
import MaterialTab from "@/components/production/techMaps/MaterialTabComponent.vue";
import ViewDetailMaterial from "@/components/production/techMaps/ViewDetailMaterialComponent.vue";
import ViewDetailTechnology from "@/components/production/techMaps/ViewDetailTechnologyComponent.vue";
import ViewDetailOperation from "@/components/production/techMaps/ViewDetailOperationComponent.vue";
import CopyStepModal from "@/components/production/techMaps/CopyStepModal.vue";
import TechnologyTab from "@/components/production/techMaps/TechnologyTabComponent.vue";
import DeleteButton from "@/components/DeleteButton.vue";
import Dropdown from "@/components/common/Dropdown.vue";
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline';

import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

export default {
  components: {
    TechnologyTab,
    ViewDetailMaterial,
    MaterialTab,
    ViewDetailTechnology,
    ViewDetailOperation,
    CopyStepModal,
    Specifications,
    TreeNode,
    Dropdown,
    vSelect,
    DeleteButton,
    ArrowTopRightOnSquareIcon,
  },
  data() {
    return {
      documentsLoaded: false,
      techMap: null,
      originalTechMap: null, // Для хранения исходной копии техкарты
      products: [],
      documents: [],
      selectedProduct: null,
      selectedTechnology: null,
      selectedOperation: null,
      technologies: [],
      activeTab: "tab1",
      activeComponent: null,
      searchQuery: '',
      addMenuSearchQuery: '',
      tabs: [
        { label: "Материалы", name: "tab1" },
        { label: "Технология", name: "tab2" },
        { label: "Документы", name: "tab3" },
      ],
      addChildOptions: [
        { label: 'Документ', value: 'file' },
        { label: 'Технология', value: 'technology' },
        { label: 'Операция', value: 'operation' },
      ],
      selectedChildOption: null,
      isModalOpen: false,
      newItemName: '',
      selectedTechnologyForOperation: null,
      selectedWorkshopPost: null,
      apiProducts: [],
      workshopPosts: [],
      addProductError: '',
      isLoadingProducts: false, // Флаг загрузки продуктов
      isSaving: false, // Флаг для блокировки сохранения
      hasUnsavedChanges: false, // Флаг для отслеживания изменений
      localIdCounter: 0, // Счетчик для генерации временных ID

      // Hover state для подсветки левой полоски технологии
      hoveredTechnologyId: null,

      // Загрузка файлов
      isFileDragOver: false,
      fileUploadProgress: '',
      pendingFiles: [],

      // Модальные окна подтверждения
      isSaveConfirmModalOpen: false,
      isCancelConfirmModalOpen: false,

      // Копирование шага
      copyStepModalOpen: false,
      copyStepSource: null,

      // Редактирование названия спецификации
      isEditingName: false,

      productSearchTimer: null
    };
  },
  computed: {
    // В маршруте лежит id спецификации, не id модуля: берём модуль «Производство»
    // из модулей пользователя по ключу production
    productionModuleId() {
      const modules = useAuthStore().user?.modules || [];
      return modules.find(m => m.key === 'production')?.id ?? null;
    },
    filteredFiles() {
      const attachments = this.techMap?.file_attachments || this.techMap?.fileAttachments || [];
      const files = attachments.map(a => a.file || a).filter(f => f);
      if (!this.searchQuery) {
        return files;
      }
      const q = this.searchQuery.toLowerCase();
      return files.filter(f => (f.original_name || f.name || '').toLowerCase().includes(q));
    },
    filteredProducts() {
      if (!this.searchQuery) {
        return this.products;
      }
      return this.products.filter(product => {
        return this.containsSearchTerm(product);
      });
    },
    filteredTechnologies() {
      if (!this.searchQuery) {
        return this.technologies;
      }
      return this.technologies.filter(technology => {
        return this.containsSearchTerm(technology);
      });
    },
    // Материалы — только позиции из шагов комплектования (type = picking),
    // как в рантайме в OrderShow.
    aggregatedMaterials() {
      const map = {};
      (this.technologies || []).forEach(comp => {
        const ops = comp.technology?.operations || [];
        ops.forEach(op => {
          (op.steps || []).forEach(step => {
            if (step.type !== 'picking') return;
            (step.products || []).forEach(product => {
              if (!product.id) return;
              if (map[product.id]) {
                map[product.id].count += Number(product.count) || 0;
              } else {
                map[product.id] = {
                  product_id: product.id,
                  name: product.name,
                  sku: product.sku,
                  count: Number(product.count) || 0,
                  is_single: !!product.is_single,
                  unit_short_name: product.unit_short_name || 'шт',
                  deleted_at: product.deleted_at,
                  deletion_mark: !!product.deletion_mark,
                };
              }
            });
          });
        });
      });
      return Object.values(map);
    },
    technologyOptions() {
      return this.technologies.map(tech => ({
        label: tech.technology.name,
        value: tech.technology_id || tech.technology.id
      }));
    },
    productOptions() {
      if (this.apiProducts.length === 0) {
        return [];
      }
      
      return this.apiProducts.map((item) => {
        return {
          id: item.id,
          name: item.name || '',
          sku: item.sku || '',
          unit_short_name: item.unit?.short_name || 'шт',
          accounting_type: item.accounting_type || null,
          label: this.getProductDisplayName(item),
          value: item.id
        };
      });
    },
    workshopPostOptions() {
      return this.workshopPosts ? this.workshopPosts.map(post => ({
        id: post.id,
        name: post.name
      })) : [];
    },
    // Computed свойства для отслеживания активных элементов
    activeProductId() {
      return this.activeComponent === 'viewDetailMaterial' && this.selectedProduct
        ? this.selectedProduct.product_id
        : null;
    },
    activeTechnologyName() {
      return (this.activeComponent === 'viewDetailTechnology' || this.activeComponent === 'viewDetailOperation') && this.selectedTechnology
        ? this.selectedTechnology.technology.name
        : null;
    },
    activeOperationId() {
      return this.activeComponent === 'viewDetailOperation' && this.selectedOperation
        ? this.selectedOperation.id
        : null;
    },
    activeTechnologyId() {
      return (this.activeComponent === 'viewDetailTechnology' || this.activeComponent === 'viewDetailOperation') && this.selectedTechnology
        ? (this.selectedTechnology.technology_id || this.selectedTechnology.technology.id)
        : null;
    }
  },
  async mounted() {
    this.apiProducts = [];
    await this.getTechMap();
  },
  watch: {
    apiProducts: {
      handler(newVal, oldVal) {
        // Данные обновились
      },
      deep: true
    }
  },
  methods: {
    getProductDisplayName(product) {
      const hasSku = product.sku && product.sku.trim() !== '';
      const hasName = product.name && product.name.trim() !== '';
      
      if (hasSku && hasName) {
        return `${product.sku} - ${product.name}`;
      } else if (hasSku) {
        return product.sku;
      } else if (hasName) {
        return product.name;
      } else {
        return 'Без названия';
      }
    },
    
    // Генерирует уникальный локальный ID
    generateLocalId() {
      this.localIdCounter++;
      return `local-${this.localIdCounter}`;
    },

    async getTechMap() {
      try {
        const { data } = await axios.post(`/api/techMap/getTechMap/${this.$route.params.id}`);
        this.techMap = data.techMap;
        this.originalTechMap = JSON.parse(JSON.stringify(data.techMap));
        this.workshopPosts = data.workshopPosts || [];

        // Инициализация документов
        this.documents = data.documents || [];
        this.documentsLoaded = this.documents.length > 0;

        this.products = this.techMap.components?.filter(el => el?.type === 'product') || [];
        this.technologies = this.techMap.components?.filter(el => el?.type === 'technology') || [];

        // Если документы не пришли, загружаем их отдельно
        if (!this.documentsLoaded) {
          await this.loadDocuments();
        }

        // ... (остальная логика инициализации) ...
      } catch (error) {
        console.error('Ошибка загрузки техкарты:', error);
      }
    },

    async loadDocuments() {
      try {
        const { data } = await axios.get('/api/documents');
        this.documents = data || [];
        this.documentsLoaded = true;
      } catch (error) {
        console.error('Ошибка загрузки документов:', error);
        this.documents = [];
        this.documentsLoaded = false;
      }
    },

    async viewDetailOperation(id, technologyId) {
      const alreadyOpen =
        this.activeComponent === 'viewDetailOperation' &&
        this.selectedOperation?.id == id;

      if (alreadyOpen) {
        this.$nextTick(() => this.scrollDetailPanelToTop());
        return;
      }

      this.activeComponent = 'viewDetailOperation';
      this.activeTab = "tab2";
      this.documentsLoaded = false; // Сбрасываем флаг загрузки

      // Загружаем первые 50 продуктов для модалки выбора продукции
      this.onProductSearch('');

      // Находим технологию
      this.selectedTechnology = this.technologies.find(el =>
        el.technology_id == technologyId || el.technology.id == technologyId
      );

      if (!this.selectedTechnology) {
        console.error('Технология не найдена');
        return;
      }

      // Находим операцию
      this.selectedOperation = this.selectedTechnology.technology.operations.find(el => el.id == id);

      if (!this.selectedOperation) {
        console.error('Операция не найдена');
        return;
      }

      // Инициализируем шаги
      if (!this.selectedOperation.steps) {
        this.selectedOperation.steps = [];
      }

      // Загружаем документы, если они еще не загружены
      if (!this.documentsLoaded) {
        await this.loadDocuments();
      }

      this.$nextTick(() => this.scrollDetailPanelToTop());
    },

    scrollDetailPanelToTop() {
      const panel = document.querySelector('[data-detail-panel]');
      if (panel) {
        panel.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    async viewDetailStep({ stepId, operationId, technologyId }) {
      const sameOperationOpen =
        this.activeComponent === 'viewDetailOperation' &&
        this.selectedOperation?.id == operationId;

      if (!sameOperationOpen) {
        await this.viewDetailOperation(operationId, technologyId);
      }

      this.$nextTick(() => {
        const tryScroll = (attempt = 0) => {
          const el = document.getElementById(`step-${stepId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else if (attempt < 10) {
            setTimeout(() => tryScroll(attempt + 1), 50);
          }
        };
        tryScroll();
      });
    },

    onProductSearch(search) {
      const doSearch = async () => {
        this.isLoadingProducts = true;
        this.addProductError = '';
        try {
          const { data } = await axios.post('/api/product/getProducts', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          });
          this.apiProducts = data?.data?.data || [];
        } catch (error) {
          console.error('Ошибка загрузки продуктов:', error);
          this.addProductError = 'Ошибка при загрузке списка продуктов.';
          this.apiProducts = [];
        } finally {
          this.isLoadingProducts = false;
        }
      };
      clearTimeout(this.productSearchTimer);
      if (!search || !search.trim()) {
        doSearch();
      } else {
        this.productSearchTimer = setTimeout(doSearch, 350);
      }
    },

    async reloadProducts() {
      this.onProductSearch('');
    },

    async addNewItem() {
      if (this.isSaving) return;

      this.isSaving = true;
      this.addProductError = '';

      let type = this.selectedChildOption;
      let techMapId = this.techMap.id;

      if (type === 'material') {
        if (!this.newItemName) {
          this.addProductError = 'Заполните все поля.';
          this.isSaving = false;
          return;
        }
        const productId = this.newItemName.value ?? this.newItemName.id;

        // Проверяем, существует ли уже такой продукт в списке
        const existingProduct = this.techMap.components.find(
          (component) => component.type === 'product' && component.product?.id === productId
        );
        if (existingProduct) {
          this.addProductError = 'Этот продукт уже добавлен.';
          this.isSaving = false;
          return;
        }

        const product = {
          id: productId,
          name: this.newItemName.name || ''
        };

        if (!this.techMap.components) {
          this.techMap.components = [];
        }

        this.techMap.components.push({
          product: {
            id: product.id,
            name: product.name
          },
          product_id: product.id,
          count: 1,
          type: 'product'
        });
        this.products = this.techMap.components?.filter(el => el.type === 'product') || [];

      } else if (type === 'technology') {
        if (!this.newItemName) {
          this.addProductError = 'Заполните название технологии.';
          this.isSaving = false;
          return;
        }
        // Проверка на уникальность имени технологии
        const existingTechnology = this.techMap.components.find(
          (component) => component.type === 'technology' &&
          component.technology?.name?.toLowerCase().trim() === this.newItemName.toLowerCase().trim()
        );

        if (existingTechnology) {
          this.addProductError = 'Технология с таким названием уже существует.';
          this.isSaving = false;
          return;
        }

        const localId = this.generateLocalId();

        if (!this.techMap.components) {
          this.techMap.components = [];
        }

        this.techMap.components.push({
          technology: {
            id: localId,
            name: this.newItemName.trim(),
            operations: []
          },
          technology_id: localId,
          type: 'technology'
        });

        this.technologies = this.techMap.components?.filter(el => el.type === 'technology') || [];

      } else if (type === 'operation') {
        // Ищем операцию по имени в рамках технологии или создаем новую
        if (!this.newItemName || !this.selectedWorkshopPost || !this.selectedTechnologyForOperation) {
          this.addProductError = 'Заполните все поля.';
          this.isSaving = false;
          return;
        }

        const technology = this.techMap.components.find(el =>
          el.type === 'technology' &&
          (el.technology_id === this.selectedTechnologyForOperation ||
           el.technology.id === this.selectedTechnologyForOperation)
        );

        // Проверяем, существует ли уже операция с таким именем в этой технологии
        const existingOperation = technology?.technology?.operations?.find(
          operation => operation.name?.toLowerCase().trim() === this.newItemName.toLowerCase().trim()
        );

        if (existingOperation) {
          this.addProductError = 'Операция с таким названием уже существует в этой технологии.';
          this.isSaving = false;
          return;
        }

        let maxSort = 0;
        if (technology?.technology?.operations && technology.technology.operations.length > 0) {
          maxSort = Math.max(...technology.technology.operations.map(op => op.sort || 0)) || 0;
        }
        const newSort = maxSort + 5;
        const localId = this.generateLocalId();

        if (!technology?.technology?.operations) {
          technology.technology.operations = [];
        }

        const newOperation = {
          id: localId,
          name: this.newItemName.trim(),
          description: '',
          workshop_post_id: this.selectedWorkshopPost,
          sort: newSort,
          steps: [],
          print_files: [],
          technology_id: this.selectedTechnologyForOperation
        };

        technology.technology.operations.push(newOperation);
        technology.technology.operations.sort((a, b) => (a.sort || 0) - (b.sort || 0));
      }

      try {
        this.addProductError = ''; // Сброс ошибки перед отправкой
        this.hasUnsavedChanges = true;
        this.closeModal();
      } catch (error) {
        console.error('Ошибка при добавлении:', error);
        this.addProductError = error.response?.data?.message || 'Произошла ошибка при добавлении.';
      } finally {
        this.isSaving = false;
      }
    },

    async saveTechMap() {
      if (this.isSaving) return;

      this.isSaving = true;
      try {
        const selectedTechId = this.selectedTechnology
          ? (this.selectedTechnology.technology_id || this.selectedTechnology.technology?.id)
          : null;
        const selectedOpId = this.selectedOperation ? this.selectedOperation.id : null;

        const response = await axios.post('/api/techMap/saveTechMap', this.techMap);

        if (response?.data?.techMap) {
          this.techMap = response.data.techMap;

          // Гарантируем, что у всех шагов есть documents: []
          this.techMap.components?.forEach(component => {
            if (component.type === 'technology' && component.technology?.operations) {
              component.technology.operations.forEach(operation => {
                operation.steps?.forEach(step => {
                  if (!Array.isArray(step.documents)) {
                    step.documents = [];
                  }
                });
              });
            }
          });

          // Обновляем списки products и technologies
          this.products = this.techMap.components?.filter(el => el?.type === 'product') || [];
          this.technologies = this.techMap.components?.filter(el => el?.type === 'technology') || [];

          // Восстанавливаем выбранную технологию и операцию
          if (selectedTechId) {
            this.selectedTechnology = this.technologies.find(el =>
              el.technology_id == selectedTechId || el.technology?.id == selectedTechId
            );

            if (this.selectedTechnology && selectedOpId && this.selectedTechnology.technology) {
              this.selectedOperation = this.selectedTechnology.technology.operations?.find(
                op => op.id == selectedOpId
              ) || null;
            }
          }
        } else {
          await this.getTechMap();
        }

        this.hasUnsavedChanges = false;
        this.originalTechMap = JSON.parse(JSON.stringify(this.techMap));

      } catch (error) {
        console.error('Error saving tech map:', error);
        let errorMsg = 'Ошибка при сохранении спецификации';

        if (error?.response?.data?.message) {
          errorMsg = error.response.data.message;
        }

        if (this.$toast && typeof this.$toast.error === 'function') {
          this.$toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        this.isSaving = false;
      }
    },

    removeMaterial(productId) {
      // Удаляем материал из компонентов
      this.techMap.components = this.techMap.components.filter(component =>
        !(component.type === 'product' && component.product_id === productId)
      );

      // Удаляем материал из шагов операций
      this.techMap.components.forEach(component => {
        if (component.type === 'technology' && component.technology?.operations) {
          component.technology.operations.forEach(operation => {
            if (operation.steps) {
              operation.steps = operation.steps.filter(step =>
                !(step.product_id === productId)
              );
            }
          });
        }
      });

      this.products = this.techMap.components?.filter(el => el.type === 'product') || [];
      this.hasUnsavedChanges = true;
    },

    updateMaterialCount(index, newCount) {
      this.techMap.components[index].count = newCount;
      this.hasUnsavedChanges = true;
      this.activeComponent = null;
    },

    removeTechnology(technologyName) {
      console.log('Удаление технологии:', technologyName);

      // Находим индекс технологии для удаления
      const technologyToRemove = this.techMap.components.find(component =>
        component.type === 'technology' && component.technology.name === technologyName
      );

      if (!technologyToRemove) {
        console.error('Технология не найдена:', technologyName);
        return;
      }

      // Запоминаем ID технологии для логирования
      const techId = technologyToRemove.technology_id || technologyToRemove.technology.id;
      console.log('Удаляемая технология:', {
        name: technologyName,
        id: techId,
        operations: technologyToRemove.technology.operations ? technologyToRemove.technology.operations.length : 0
      });

      // Удаляем технологию из компонентов по имени
      this.techMap.components = this.techMap.components.filter(component =>
        !(component.type === 'technology' && component.technology.name === technologyName)
      );

      // Обновляем список технологий
      this.technologies = this.techMap.components?.filter(el => el.type === 'technology') || [];

      // Если удалили текущую выбранную технологию, сбрасываем её
      if (this.selectedTechnology?.technology.name === technologyName) {
        this.selectedTechnology = null;
        this.activeComponent = null;

        // Дополнительно форсируем обновление интерфейса
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }

      // Обязательно отмечаем, что есть несохраненные изменения
      this.hasUnsavedChanges = true;

      console.log('Технология удалена, осталось технологий:', this.technologies.length);
    },

    removeOperation(operationId, technologyId) {
      console.log('removeOperation в TechMap.vue', { operationId, technologyId });

      // Находим технологию в массиве компонентов
      const technology = this.techMap.components.find(el =>
        el.type === 'technology' &&
        (el.technology.id === technologyId || el.technology_id === technologyId)
      );

      if (!technology) {
        console.error('Технология не найдена', technologyId);
        return;
      }

      if (!technology.technology.operations) {
        console.error('Операции отсутствуют в технологии', technology);
        return;
      }

      // Конвертируем ID в строковый формат для надежного сравнения
      const operationIdStr = String(operationId);

      // Находим операцию, которую нужно удалить
      const operationToRemove = technology.technology.operations.find(op => {
        return String(op.id) === operationIdStr ||
              (typeof op.id === 'string' && op.id.includes('local-') && op.id === operationIdStr);
      });

      if (!operationToRemove) {
        console.error('Операция с ID не найдена', operationId);
        console.log('Доступные операции:', technology.technology.operations.map(op => ({
          id: op.id,
          name: op.name,
          isLocal: typeof op.id === 'string' && op.id.includes('local-')
        })));
        return;
      }

      // Запоминаем ID операции для отладки и сравнения
      const opIdToRemove = operationToRemove.id;
      console.log('Удаляем операцию:', {
        id: opIdToRemove,
        name: operationToRemove.name,
        sort: operationToRemove.sort
      });

      // Создаем новый массив операций, исключая удаляемую операцию
      const filteredOperations = technology.technology.operations.filter(op =>
        String(op.id) !== String(opIdToRemove)
      );

      // Создаем копию технологии с обновленным массивом операций
      const updatedTechnology = {
        ...technology,
        technology: {
          ...technology.technology,
          operations: filteredOperations
        }
      };

      // Обновляем технологию в списке компонентов
      const updatedComponents = this.techMap.components.map(comp => {
        if (comp.type === 'technology' &&
          (comp.technology.id === technologyId || comp.technology_id === technologyId)) {
          return updatedTechnology;
        }
        return comp;
      });

      // Обновляем техкарту
      this.techMap = {
        ...this.techMap,
        components: updatedComponents
      };

      // Обновляем список технологий
      this.technologies = this.techMap.components.filter(el => el.type === 'technology');

      // Если удаляется текущая выбранная операция, очищаем выбор
      if (this.selectedOperation && String(this.selectedOperation.id) === String(opIdToRemove)) {
        this.selectedOperation = null;
        this.activeComponent = 'viewDetailTechnology';
      }

      // Если открыта страница просмотра технологии, обновляем данные выбранной технологии
      if (this.activeComponent === 'viewDetailTechnology' && this.selectedTechnology) {
        // Ищем обновленную технологию в списке технологий
        const updatedSelectedTech = this.technologies.find(tech =>
          (tech.technology_id === technologyId || tech.technology.id === technologyId)
        );

        if (updatedSelectedTech) {
          // Обновляем selectedTechnology новым объектом, чтобы triggerить реактивность
          this.selectedTechnology = {...updatedSelectedTech};
          console.log('Обновлена selectedTechnology после удаления операции', {
            techId: technologyId,
            operationsCount: this.selectedTechnology.technology.operations.length
          });

          // Дополнительно форсируем обновление через setTimeout
          setTimeout(() => {
            this.$forceUpdate();
          }, 0);
        }
      }

      this.hasUnsavedChanges = true;
    },

    switchTab(tabName) {
      this.activeTab = tabName;
      this.activeComponent = null;
      // Сбрасываем выбранные элементы при переключении табов
      this.selectedProduct = null;
      this.selectedTechnology = null;
      this.selectedOperation = null;
    },

    viewDetailMaterial(id) {
      this.activeComponent = 'viewDetailMaterial';
      this.activeTab = "tab1";
      this.selectedProduct = this.products.find(el => el.product_id == id);
    },

    viewDetailTechnology(name) {
      this.activeComponent = 'viewDetailTechnology';
      this.activeTab = "tab2";
      this.selectedTechnology = this.technologies.find(el => el.technology.name === name);
    },

    setHoveredTechnology(technologyId) {
      this.hoveredTechnologyId = technologyId;
    },

    async moveOperationUp(operationId, technologyId) {
      // Находим индекс технологии
      const technologyIndex = this.techMap.components.findIndex(el =>
        el.type === 'technology' &&
        (el.technology.id === technologyId || el.technology_id === technologyId)
      );

      if (technologyIndex === -1) {
        return;
      }

      const technology = this.techMap.components[technologyIndex];

      if (!technology || !technology.technology.operations) {
        return;
      }

      const operations = technology.technology.operations;
      const currentIndex = operations.findIndex(op => op.id === operationId);

      if (currentIndex <= 0) {
        return;
      }

      const currentOperation = operations[currentIndex];
      const previousOperation = operations[currentIndex - 1];

      if (!currentOperation || !previousOperation) {
        console.error('Операция не найдена');
        return;
      }

      // Инициализируем sort, если он не определен
      if (currentOperation.sort === undefined) currentOperation.sort = currentIndex * 10;
      if (previousOperation.sort === undefined) previousOperation.sort = (currentIndex - 1) * 10;

      // Создаем копии операций с измененными sort
      const updatedCurrentOperation = { ...currentOperation, sort: previousOperation.sort };
      const updatedPreviousOperation = { ...previousOperation, sort: currentOperation.sort };

      // Создаем новый массив операций
      const updatedOperations = [...operations];
      updatedOperations[currentIndex] = updatedCurrentOperation;
      updatedOperations[currentIndex - 1] = updatedPreviousOperation;

      // Сортируем массив по полю sort
      updatedOperations.sort((a, b) => (a.sort || 0) - (b.sort || 0));

      // Создаем обновленную технологию с новым массивом операций
      const updatedTechnology = {
        ...technology,
        technology: {
          ...technology.technology,
          operations: updatedOperations
        }
      };

      // Обновляем компоненты техкарты
      const updatedComponents = [...this.techMap.components];
      updatedComponents[technologyIndex] = updatedTechnology;

      // Обновляем техкарту целиком
      this.techMap = {
        ...this.techMap,
        components: updatedComponents
      };

      // Обновляем список технологий
      this.technologies = this.techMap.components.filter(el => el.type === 'technology');

      // Если перемещается выбранная операция, обновляем её
      if (this.selectedOperation && this.selectedOperation.id === operationId) {
        this.selectedOperation = updatedCurrentOperation;
      }

      // Если открыта страница просмотра технологии, обновляем выбранную технологию
      if (this.activeComponent === 'viewDetailTechnology' && this.selectedTechnology) {
        this.selectedTechnology = this.technologies.find(el =>
          el.technology_id === technologyId || el.technology.id === technologyId
        );

        if (this.selectedTechnology) {
          // Создаем новый объект, чтобы триггерить реактивность
          this.selectedTechnology = {...this.selectedTechnology};
          console.log('Обновлена selectedTechnology после перемещения операции вверх', {
            techId: technologyId,
            operationsCount: this.selectedTechnology.technology.operations.length,
            operations: this.selectedTechnology.technology.operations.map(op => ({
              id: op.id,
              name: op.name,
              sort: op.sort
            }))
          });

          // Дополнительно форсируем обновление через setTimeout
          setTimeout(() => {
            this.$forceUpdate();
          }, 0);
        }
      }

      this.hasUnsavedChanges = true;
    },

    async moveOperationDown(operationId, technologyId) {
      // Находим индекс технологии
      const technologyIndex = this.techMap.components.findIndex(el =>
        el.type === 'technology' &&
        (el.technology.id === technologyId || el.technology_id === technologyId)
      );

      if (technologyIndex === -1) {
        return;
      }

      const technology = this.techMap.components[technologyIndex];

      if (!technology || !technology.technology.operations) {
        return;
      }

      const operations = technology.technology.operations;
      const currentIndex = operations.findIndex(op => op.id === operationId);

      if (currentIndex >= operations.length - 1) {
        return;
      }

      const currentOperation = operations[currentIndex];
      const nextOperation = operations[currentIndex + 1];

      if (!currentOperation || !nextOperation) {
        console.error('Операция не найдена');
        return;
      }

      // Инициализируем sort, если он не определен
      if (currentOperation.sort === undefined) currentOperation.sort = currentIndex * 10;
      if (nextOperation.sort === undefined) nextOperation.sort = (currentIndex + 1) * 10;

      // Создаем копии операций с измененными sort
      const updatedCurrentOperation = { ...currentOperation, sort: nextOperation.sort };
      const updatedNextOperation = { ...nextOperation, sort: currentOperation.sort };

      // Создаем новый массив операций
      const updatedOperations = [...operations];
      updatedOperations[currentIndex] = updatedCurrentOperation;
      updatedOperations[currentIndex + 1] = updatedNextOperation;

      // Сортируем массив по полю sort
      updatedOperations.sort((a, b) => (a.sort || 0) - (b.sort || 0));

      // Создаем обновленную технологию
      const updatedTechnology = {
        ...technology,
        technology: {
          ...technology.technology,
          operations: updatedOperations
        }
      };

      // Обновляем компоненты техкарты
      const updatedComponents = [...this.techMap.components];
      updatedComponents[technologyIndex] = updatedTechnology;

      // Обновляем техкарту целиком
      this.techMap = {
        ...this.techMap,
        components: updatedComponents
      };

      // Обновляем список технологий
      this.technologies = this.techMap.components.filter(el => el.type === 'technology');

      // Если перемещается выбранная операция, обновляем её
      if (this.selectedOperation && this.selectedOperation.id === operationId) {
        this.selectedOperation = updatedCurrentOperation;
      }

      // Если открыта страница просмотра технологии, обновляем выбранную технологию
      if (this.activeComponent === 'viewDetailTechnology' && this.selectedTechnology) {
        this.selectedTechnology = this.technologies.find(el =>
          el.technology_id === technologyId || el.technology.id === technologyId
        );

        if (this.selectedTechnology) {
          // Создаем новый объект, чтобы триггерить реактивность
          this.selectedTechnology = {...this.selectedTechnology};
          console.log('Обновлена selectedTechnology после перемещения операции вниз', {
            techId: technologyId,
            operationsCount: this.selectedTechnology.technology.operations.length,
            operations: this.selectedTechnology.technology.operations.map(op => ({
              id: op.id,
              name: op.name,
              sort: op.sort
            }))
          });

          // Дополнительно форсируем обновление через setTimeout
          setTimeout(() => {
            this.$forceUpdate();
          }, 0);
        }
      }

      this.hasUnsavedChanges = true;
    },

    containsSearchTerm(node) {
      const searchTerm = this.searchQuery.toLowerCase();
      if (node.name && node.name.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (node.product?.name && node.product.name.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (node.technology?.name && node.technology.name.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (node.technology?.operations?.length) {
        for (const operation of node.technology.operations) {
          if (operation.name && operation.name.toLowerCase().includes(searchTerm)) {
            return true;
          }
          if (operation.steps) {
            for (const step of operation.steps) {
              if (step.name && step.name.toLowerCase().includes(searchTerm)) {
                return true;
              }
            }
          }
        }
      }
      return false;
    },

    handleOptionSelect(option) {
      if (option) {
        this.selectedChildOption = option;
        this.isModalOpen = true;

        // Загружаем первые 50 продуктов при открытии модального окна для материалов
        if (option === 'material') {
          this.onProductSearch('');
        }
      }
    },

    openModal() {
      if (this.selectedChildOption) {
        this.isModalOpen = true;
      }
    },

    closeModal() {
      this.isModalOpen = false;
      this.selectedChildOption = null;
      this.newItemName = '';
      this.selectedTechnologyForOperation = null;
      this.selectedWorkshopPost = null;
      this.addProductError = '';
      this.isFileDragOver = false;
      this.fileUploadProgress = '';
      this.pendingFiles = [];
    },

    handleFileDrop(e) {
      this.isFileDragOver = false;
      const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf');
      this.uploadAndAttachFiles(files);
    },

    handleFileSelect(e) {
      const files = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
      this.uploadAndAttachFiles(files);
      e.target.value = '';
    },

    async uploadAndAttachFiles(files) {
      if (!files.length) {
        this.addProductError = 'Выберите PDF-файлы';
        return;
      }
      this.addProductError = '';
      for (let i = 0; i < files.length; i++) {
        this.fileUploadProgress = `Загрузка ${i + 1}/${files.length}: ${files[i].name}`;
        try {
          const formData = new FormData();
          formData.append('file', files[i]);
          const { data } = await axios.post('/api/files', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (!data.file?.id) throw new Error();
          this.pendingFiles.push(data.file);
        } catch {
          this.fileUploadProgress = '';
          this.addProductError = 'Не удалось загрузить файл. Попробуйте ещё раз';
          console.error('Ошибка загрузки файла:', err);
        }
      }
      this.fileUploadProgress = '';
    },

    confirmFileUpload() {
      if (!this.pendingFiles.length) return;
      if (!this.techMap.file_attachments) {
        this.techMap.file_attachments = [];
      }
      for (const file of this.pendingFiles) {
        this.techMap.file_attachments.push({ file_id: file.id, file });
      }
      this.pendingFiles = [];
      this.hasUnsavedChanges = true;
      this.closeModal();
    },

    getSelectedOptionLabel() {
      const selectedOption = this.addChildOptions.find(option => option.value === this.selectedChildOption);
      return selectedOption ? selectedOption.label : '';
    },

    viewDetailFile() {
      this.activeTab = 'tab3';
      this.activeComponent = null;
    },

    openFilePreview(file) {
      if (file?.id) {
        window.open(`/api/files/${file.id}/preview`, '_blank');
      }
    },

    removeFile(index) {
      const attachments = this.techMap.file_attachments || this.techMap.fileAttachments || [];
      attachments.splice(index, 1);
      this.hasUnsavedChanges = true;
    },

    handleTechnologyChange() {
      console.log('Изменения в технологии');
      this.hasUnsavedChanges = true;
    },

    handleOperationChange() {
      this.hasUnsavedChanges = true;

      if (this.selectedTechnology && this.selectedOperation) {
        const operations = this.selectedTechnology.technology.operations;

        const index = operations.findIndex(op => op.id === this.selectedOperation.id);

        if (index !== -1) {
          // Обновляем текущую операцию
          this.selectedTechnology.technology.operations.splice(index, 1, { ...this.selectedOperation });
        }
      }
    },

    startEditingName() {
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.techMapNameInput?.focus();
      });
    },

    openCopyStepModal(step) {
      this.copyStepSource = step;
      this.copyStepModalOpen = true;
    },

    closeCopyStepModal() {
      this.copyStepModalOpen = false;
      this.copyStepSource = null;
    },

    onStepCopyConfirmed({ name, type, targetOperationId }) {
      const source = this.copyStepSource;
      if (!source) {
        this.closeCopyStepModal();
        return;
      }

      // Ищем целевую операцию во всех технологиях спеки
      let targetOperation = null;
      for (const component of (this.technologies || [])) {
        const operations = component.technology?.operations || [];
        const found = operations.find(op => op.id === targetOperationId);
        if (found) {
          targetOperation = found;
          break;
        }
      }

      if (!targetOperation) {
        alert('Не удалось найти выбранную операцию');
        return;
      }

      if (!Array.isArray(targetOperation.steps)) {
        targetOperation.steps = [];
      }

      const maxSort = targetOperation.steps.length
        ? Math.max(...targetOperation.steps.map(s => s.sort || 0))
        : 0;
      const nextSort = Math.ceil((maxSort + 1) / 5) * 5;

      // Документы копируем только если тип остался print
      const keepDocuments = source.type === 'print' && type === 'print';

      const newStep = {
        id: `local-step-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        name,
        type,
        description: source.description || '',
        img_url: source.img_url || null,
        sort: nextSort,
        preparation_time: source.preparation_time || 0,
        production_time: source.production_time || 0,
        products: Array.isArray(source.products) ? source.products.map(p => ({ ...p })) : [],
        documents: keepDocuments && Array.isArray(source.documents)
          ? source.documents.map(d => ({ ...d }))
          : [],
        images: Array.isArray(source.images) ? source.images.map(i => ({ ...i })) : [],
        // Маркер для бэка: после создания шага скопировать file_attachments из исходного
        _copy_from_step_id: typeof source.id === 'number' ? source.id : null,
      };

      targetOperation.steps.push(newStep);
      this.hasUnsavedChanges = true;
      this.closeCopyStepModal();
    },

    goBack() {
      this.$router.back();
    },
    revertChanges() {
      if (!this.hasUnsavedChanges) return;

      this.showCancelConfirmModal();
    },

    // Добавляем методы для работы с модальными окнами подтверждения
    showSaveConfirmModal() {
      this.isSaveConfirmModalOpen = true;
    },

    closeSaveConfirmModal() {
      this.isSaveConfirmModalOpen = false;
    },

    showCancelConfirmModal() {
      this.isCancelConfirmModalOpen = true;
    },

    closeCancelConfirmModal() {
      this.isCancelConfirmModalOpen = false;
    },

    async confirmSave() {
      this.closeSaveConfirmModal();
      await this.saveTechMap();
    },

    async confirmCancel() {
      this.closeCancelConfirmModal();
      try {
        await axios.post('/api/productionOperation/clearTempImages');
      } catch (e) {
        console.warn('Ошибка очистки временных изображений:', e);
      }
      this.techMap = JSON.parse(JSON.stringify(this.originalTechMap));
      this.products = this.techMap.components?.filter(el => el.type === 'product') || [];
      this.technologies = this.techMap.components?.filter(el => el.type === 'technology') || [];
      this.selectedTechnology = null;
      this.selectedOperation = null;
      this.activeComponent = null;
      this.hasUnsavedChanges = false;

      // Проверяем, существует ли сервис уведомлений перед использованием
      if (this.$toast && typeof this.$toast.info === 'function') {
        this.$toast.info('Изменения отменены');
      } else {
        console.log('Изменения отменены');
      }
    }
  },
  async beforeUnmount() {
    if (this.hasUnsavedChanges) {
      try {
        await axios.post('/api/productionOperation/clearTempImages');
      } catch (e) {
        console.warn('Ошибка очистки временных изображений при уходе:', e);
      }
    }
  }
};
</script>
